import React from 'react';
import {IoLocationSharp} from 'react-icons/io5';
import {competitionName, isOwnTeam} from '../../data/games';
import {GameData} from '../../types';
import {DAY_NAMES, DAY_SHORT_NAMES, formatTime, MONTH_NAMES, parseLocalDateTime} from '../../utils/date';
import TeamLogo from './TeamLogo';
import './game.css';

interface GameProps {
    game: GameData;
    /** "card" for the home page, "row" for the schedule (adapts to tablet and phone via CSS) */
    variant: 'card' | 'row';
}

/** 00:00 is used for games whose time isn't fixed yet. */
const startTime = (date: Date) => (date.getHours() || date.getMinutes() ? formatTime(date) : 'tba');

const Location = ({place}: { place: string }) => place ? (
    <a className="game-location" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place)}`}
       target="_blank" rel="noopener noreferrer">
        <IoLocationSharp aria-hidden="true"/>
        <span>{place}</span>
    </a>
) : null;

const Team = ({name, side}: { name: string; side: 'home' | 'away' }) => (
    <div className={`game-team game-team--${side}${isOwnTeam(name) ? ' game-team--own' : ''}`}>
        <TeamLogo team={name}/>
        <span className="game-team-name">{name}</span>
    </div>
);

const Game = ({game, variant}: GameProps) => {
    const date = parseLocalDateTime(game.termin);
    const competition = competitionName(game.bewerb);

    if (variant === 'row') {
        const homeGame = isOwnTeam(game.heim);
        return (
            <article className={`game-row${homeGame ? ' game-row--home' : ''}`}>
                <div className="game-row-date">
                    <span className="game-row-day">{date.getDate()}</span>
                    <span className="game-row-weekday">{DAY_SHORT_NAMES[date.getDay()]}</span>
                    <span className="visually-hidden">{homeGame ? 'Heimspiel' : 'Auswärtsspiel'}</span>
                </div>
                <div className="game-row-teams">
                    <Team name={game.heim} side="home"/>
                    <span className="game-vs">vs</span>
                    <Team name={game.auswaerts} side="away"/>
                </div>
                <div className="game-row-info">
                    <span className="game-row-time">{startTime(date)}</span>
                    <span className="game-badge">{competition}</span>
                    <span className="game-row-place"><Location place={game.ort}/></span>
                </div>
            </article>
        );
    }

    return (
        <article className="game-card">
            <header className="game-card-head">
                <span className="game-card-date">
                    {DAY_NAMES[date.getDay()]}, {date.getDate()}. {MONTH_NAMES[date.getMonth()]}
                </span>
                <span className="game-card-competition">{competition}</span>
            </header>
            <div className="game-card-body">
                <Team name={game.heim} side="home"/>
                <div className="game-card-time">
                    {startTime(date)}
                    <span className="game-vs">vs</span>
                </div>
                <Team name={game.auswaerts} side="away"/>
            </div>
            {game.ort && <footer className="game-card-foot"><Location place={game.ort}/></footer>}
        </article>
    );
};

export default Game;
